import React from "react";
import { NOTIFICATION_STYLES } from "../utils/notificationStyles";
import { bucketNotifications } from "../utils/notificationBuckets";
import {
  Bell,
  X,
  AlertTriangle,
  MessageSquare,
  MapPin,
  Activity,
  BrainCircuit,
  Image,
  CheckCircle,
} from "lucide-react";

/* ==================================================================
   Notifications Drawer
================================================================== */

export default function NotificationsDrawer({
  open,
  onClose,
  notifications = [],
  onAction,
  onAcknowledge,
}) {
  const buckets = bucketNotifications(notifications);

  return (
    <div
      className={`
        fixed top-0 right-0 h-full w-96
        bg-bg-softer border-l border-slate-800
        shadow-xl z-[120]
        transform transition-transform duration-300
        ${open ? "translate-x-0" : "translate-x-full"}
      `}
    >
      {/* HEADER */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-slate-800 bg-bg-soft">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-accent-info" />
          <span className="text-[10px] font-semibold tracking-wide text-gray-200">
            Notifications
          </span>
        </div>

        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-200 transition"
        >
          <X size={18} />
        </button>
      </div>

      {/* LIST */}
      <div className="overflow-auto h-full p-2 space-y-4">
        {notifications.length === 0 && (
          <div className="text-center text-fg-secondary text-[9px] py-6">
            No notifications.
          </div>
        )}

        {renderSection("Today", buckets.today, onAction, onAcknowledge)}
        {renderSection("This Week", buckets.week, onAction, onAcknowledge)}
        {renderSection("Older", buckets.older, onAction, onAcknowledge)}
      </div>
    </div>
  );
}

/* ==================================================================
   Section Renderer
================================================================== */

function renderSection(label, items, onAction, onAcknowledge) {
  if (!items.length) return null;

  return (
    <div>
      <div className="mb-1 text-[9px] font-mono uppercase tracking-[0.18em] text-gray-400">
        {label}
      </div>

      <div className="space-y-1">
        {items.map((notif) => (
          <NotificationCard
            key={notif.id}
            notif={notif}
            onAction={onAction}
            onAcknowledge={onAcknowledge}
          />
        ))}
      </div>
    </div>
  );
}

/* ==================================================================
   Notification Card
================================================================== */

function NotificationCard({ notif, onAction, onAcknowledge }) {
  const style =
    NOTIFICATION_STYLES[notif.severity] || NOTIFICATION_STYLES.low;
  const Icon = getIcon(notif.event_type);

  const isUnread = !notif.read_at;

  const requiresAck =
    ["medium", "high", "critical"].includes(notif.severity) &&
    !notif.acknowledged_at;

  return (
    <div
      className={`
        rounded-md p-3 transition
        border ${style.border} ${style.bg}
        hover:border-accent-info/60
      `}
    >
      <div className="flex items-start gap-3">
        {/* ICON */}
        <div className={`mt-1 ${style.color}`}>
          <Icon size={18} />
        </div>

        {/* CONTENT */}
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="font-medium text-gray-200 text-xs">
              {notif.title || "Untitled event"}
            </div>

            {isUnread && (
              <span className="w-2 h-2 rounded-full bg-accent-info animate-pulse" />
            )}
          </div>

          <p className="text-xs text-gray-400 mt-1">
            {notif.message || "No details provided"}
          </p>

          <div className="mt-2 flex items-center justify-between text-[9px] text-fg-secondary">
            <span>{formatTime(notif.created_at)}</span>

            <div className="flex items-center gap-2">
              {notif.action && (
                <button
                  onClick={() => onAction?.(notif)}
                  className="flex items-center gap-1 text-accent-action hover:text-accent-action transition"
                >
                  <MapPin size={10} />
                  View
                </button>
              )}

              {requiresAck && (
                <button
                  onClick={() => onAcknowledge?.(notif)}
                  className="
                    flex items-center gap-1
                    px-2 py-[2px]
                    rounded
                    border border-yellow-500/40
                    bg-yellow-500/10
                    text-yellow-300
                    hover:bg-yellow-500/20
                  "
                >
                  <CheckCircle size={10} />
                  Ack
                </button>
              )}

              {!requiresAck && notif.acknowledged_at && (
                <span className="flex items-center gap-1 text-green-400">
                  <CheckCircle size={10} />
                  Acknowledged
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================
   Icon Resolver
================================================================== */

function getIcon(type) {
  switch (type) {
    case "alert":
      return AlertTriangle;
    case "ai":
      return BrainCircuit;
    case "ingest":
      return Image;
    case "aoi":
      return MapPin;
    case "system":
      return Activity;
    case "comment":
      return MessageSquare;
    default:
      return Bell;
  }
}

/* ==================================================================
   Time Formatter
================================================================== */

function formatTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });
}